import React, { useMemo } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import { SELLER_NAV_SECTIONS, filterVisibleSections } from './sellerNavRegistry';
import { resolveActiveNav } from '../../Nav/navActive';
import useNavDisclosure from '../../Nav/useNavDisclosure';
import SellerNavSearch from './SellerNavSearch';
import SellerNavSection from './SellerNavSection';
import SellerNavItem from './SellerNavItem';
import SellerNavChannelSwitcher from './SellerNavChannelSwitcher';
import SellerNavHelpArticles from './SellerNavHelpArticles';
import SellerNavFooter from './SellerNavFooter';
import useSellerNavAlerts from './useSellerNavAlerts';
import './SellerNav.css';

/**
 * @param {Object} props
 * @param {string} [props.activeId] Item or child id the server knows is active (from data-props).
 * @param {string} [props.activeHref] Href to match instead of the browser location.
 * @param {string} [props.logoutHref]
 * @param {Array<{value: string, label: string}>} [props.languageOptions]
 * @param {string} [props.currentLanguage]
 */
export default function SellerNavSidebar({
  activeId = null,
  activeHref = null,
  logoutHref,
  languageOptions,
  currentLanguage,
}) {
  const { channel, account } = useAuth();
  const visibleSections = useMemo(
    () => filterVisibleSections(SELLER_NAV_SECTIONS, { channel, account }),
    [channel, account],
  );

  const active = useMemo(
    () => resolveActiveNav(visibleSections, {
      activeId,
      activeHref,
      location: typeof window === 'undefined' ? null : window.location,
    }),
    [visibleSections, activeId, activeHref],
  );
  const { counts, alerts } = useSellerNavAlerts(active.itemId);

  const {
    query,
    setQuery,
    searchResult,
    isSearching,
    expandedSectionIds,
    expandedItemIds,
    toggleSection,
    toggleItem,
  } = useNavDisclosure(visibleSections, active);

  if (!channel) {
    return <div className="seller-nav-sidebar seller-nav-sidebar--loading">Loading…</div>;
  }

  const rootClassName = channel.isRed ? 'seller-nav-sidebar seller-nav-sidebar--red' : 'seller-nav-sidebar';

  return (
    <div className={rootClassName}>
      <SellerNavChannelSwitcher />
      <SellerNavSearch
        query={query}
        onQueryChange={setQuery}
        resultCount={searchResult.resultCount}
      />
      <nav className="seller-nav-sidebar__sections" aria-label="Seller tools">
        {visibleSections
          .filter(section => !isSearching || searchResult.matchingSectionIds.has(section.id))
          .map(section => (section.isPinned ? (
          <ul key={section.id} className="seller-nav-sidebar__pinned" aria-label={section.title}>
            {section.items
              .filter(item => !isSearching || searchResult.matchingItemIds.has(item.id))
              .map(item => (
                <SellerNavItem
                  key={item.id}
                  item={item}
                  isExpanded={expandedItemIds.has(item.id)}
                  onToggle={() => toggleItem(item.id)}
                  searchResult={searchResult}
                  isSearching={isSearching}
                  query={query}
                  active={active}
                  counts={counts}
                  alerts={alerts}
                />
              ))}
          </ul>
        ) : (
          <SellerNavSection
            key={section.id}
            section={section}
            isExpanded={expandedSectionIds.has(section.id)}
            onToggle={() => toggleSection(section.id)}
            expandedItemIds={expandedItemIds}
            onToggleItem={toggleItem}
            searchResult={searchResult}
            isSearching={isSearching}
            query={query}
            active={active}
            counts={counts}
            alerts={alerts}
          />
        )))}
        {isSearching && <SellerNavHelpArticles query={query} />}
      </nav>
      <SellerNavFooter
        logoutHref={logoutHref}
        languageOptions={languageOptions}
        currentLanguage={currentLanguage}
      />
    </div>
  );
}
